
import React, { createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useAuthState } from '../hooks/useAuthState';
import { useAuthActions } from '../hooks/useAuthActions';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  phone?: string;
  avatar_url?: string;
  user_type: 'renter' | 'landlord';
  roles?: ('admin' | 'landlord' | 'renter')[]; // Secure roles from user_roles table
  subscription_tier: 'renter' | 'student' | 'landlord' | 'all_in' | 'enterprise' | null;
  subscription_status: 'active' | 'trial' | 'cancelled' | 'past_due' | null;
  payment_status: 'none' | 'pending' | 'completed' | 'failed';
  payment_required: boolean;
  flock_coins: number;
  referral_code: string;
  referred_by?: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  signUp: (email: string, password: string, userData?: any) => Promise<{ error: any }>;
  register: (email: string, password: string, userData?: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  login: (email: string, password: string) => Promise<{ error: any }>;
  logout: () => Promise<void>;
  updateProfile: (data: any) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile, session, loading, isInitialized, fetchProfile } = useAuthState();
  const { signUp, signIn, logout, updateProfile: updateProfileAction } = useAuthActions();

  const updateProfile = async (data: any) => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }
    return updateProfileAction(data, user.id, fetchProfile);
  };

  const value = {
    user,
    profile,
    session,
    loading,
    isLoading: loading,
    isInitialized,
    signUp,
    register: signUp,
    signIn,
    login: signIn,
    logout,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
